/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { Button, Center, Group, Loader, Text, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconEye, IconLock, IconTrash, IconUsers } from '@tabler/icons-react';
import Cookies from 'js-cookie';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Declarant, IUsersCriteria, Role, User } from '@e-origin/shared';

import { getInitialUserCriteria, useAdminDeleteUser, useAdminImpersonateUser, useAdminUsers, useDeclarants, useUserCriteriaUrlSync } from '@admin/hooks';
import {
  CheckedColumn,
  ListFilterChangeEvent,
  ListFilterDropdown,
  ListFilterDropdownElements,
  SearchBox,
  Table,
  TableActions,
  TableColumnProps,
} from '@components';
import { AUTH_COOKIE_NAME } from '@constants';
import { ContentLayout } from '@features/layout';
import { removeUndefined } from '@utils';
import { ChangePasswordModal } from './ChangePasswordModal';
import { EditUser } from './EditUser';

export const ManageUsers = () => {
  const theme = useMantineTheme();
  const [searchParams] = useSearchParams();
  const initialCriteria = getInitialUserCriteria(searchParams);

  const [filters, setFilters] = useState<IUsersCriteria['filters']>(initialCriteria.filters);
  const [sorting, setSorting] = useState<IUsersCriteria['sorting']>(initialCriteria.sorting);
  const [pagination, setPagination] = useState<IUsersCriteria['pagination']>(initialCriteria.pagination);
  useUserCriteriaUrlSync({ filters, sorting, pagination });
  const { data: declarantsList } = useDeclarants();
  const [declarantsOptions, setDeclarantsOptions] = useState<ListFilterDropdownElements>({});
  const { data: { data: { users = [], totalItems = 0 } = {} } = {}, isLoading } = useAdminUsers({
    filters,
    sorting,
    pagination,
  });
  const deleteUserMutation = useAdminDeleteUser();
  const impersonateUserMutation = useAdminImpersonateUser();

  useEffect(() => {
    setDeclarantsOptions(buildDeclarantsDropdownOptions(declarantsList));
  }, [declarantsList, filters.filterByDeclarants]);

  const buildDeclarantsDropdownOptions = (list) => {
    const declarantsDropdownOptionList: ListFilterDropdownElements = {};

    list?.forEach((dec) => {
      declarantsDropdownOptionList[dec._id] = {
        label: dec.accountName,
        checked: filters.filterByDeclarants ? filters.filterByDeclarants.includes(dec._id) : true,
      };
    });

    return declarantsDropdownOptionList;
  };

  const setNewFilter = (field: string, value: string | string[]) => {
    const mergedFilters = removeUndefined({
      ...filters,
      ...{ [field]: value },
    }) as IUsersCriteria['filters'];

    setFilters(mergedFilters);
    setPagination({ ...pagination, page: 1 });
  };

  const filterByDeclarant = (declarantOption: ListFilterChangeEvent) => {
    if (!declarantsList) {
      return;
    }

    let filterByDeclarants: string[];
    if (!declarantOption.id) {
      if (declarantOption.checked) {
        filterByDeclarants = declarantsList.map((usr) => usr._id);
      } else {
        filterByDeclarants = [];
      }
    } else {
      filterByDeclarants = declarantsList.reduce((acc, cur) => {
        if ((declarantOption.id === cur._id && declarantOption.checked) || (filters.filterByDeclarants?.includes(cur._id) && declarantOption.id !== cur._id)) {
          acc.push(cur._id);
        }
        return acc;
      }, []);
    }

    if (!isEqual(filterByDeclarants, filters.filterByDeclarants)) {
      setNewFilter('filterByDeclarants', filterByDeclarants);
    }
  };

  const openDeleteUser = (user: User) => {
    modals.openConfirmModal({
      title: 'Delete User',
      children: <Text>Are you sure you want to delete this user?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteUserMutation.mutate({ params: { email: user.email } });
      },
    });
  };

  const openUserModal = (user?: User) => {
    modals.open({
      title: user ? 'Edit User' : 'New User',
      children: <EditUser user={user} onHide={() => modals.closeAll()} />,
      size: 'lg',
    });
  };

  const openChangePasswordModal = (user: User) => {
    modals.open({
      title: 'Change Password',
      children: <ChangePasswordModal user={user} onHide={() => modals.closeAll()} />,
      size: 'lg',
    });
  };

  const handleSearch = (searchQuery: string) => {
    setFilters((prev) => ({
      ...prev,
      searchQuery,
    }));
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handleImpersonate = async (user: User) => {
    const {
      body: { token },
    } = await impersonateUserMutation.mutateAsync({ body: { email: user.email } });
    Cookies.set(AUTH_COOKIE_NAME, token);

    const url = window.location.origin + window.location.pathname.replace('/admin', '');
    window.open(url, '_blank');
  };

  const columns: TableColumnProps<User>[] = [
    {
      name: 'Email',
      cell: (row) => row.email,
      sortable: true,
      selector: 'email',
    },
    {
      name: 'First Name',
      cell: (row) => row.firstName,
      sortable: true,
      selector: 'firstName',
    },
    {
      name: 'Last Name',
      cell: (row) => row.lastName,
      sortable: true,
      selector: 'lastName',
    },
    {
      name: 'Declarant account name',
      cell: (row) => (row.declarant as unknown as Declarant)?.accountName || 'N/A',
    },
    {
      name: 'Declarant name',
      cell: (row) => (row.declarant as unknown as Declarant)?.name || 'N/A',
    },
    {
      name: 'Role',
      cell: (row) => (row.role as unknown as Role)?.name || 'N/A',
    },
    {
      name: 'Visible',
      cell: (row) => <CheckedColumn checked={row.visible !== false} />,
      sortable: true,
      selector: 'visible',
    },
    {
      name: 'Active',
      cell: (row) => <CheckedColumn checked={row.deleted !== true} />,
      sortable: true,
      selector: 'deleted',
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row) => (
        <TableActions
          actions={[
            {
              // icon: viewIcon,
              iconComponent: <IconEye color={theme.colors[theme.primaryColor][6]} />,
              text: 'View User',
              onClick: () => openUserModal(row),
            },
            {
              // icon: lockIcon,
              iconComponent: <IconLock color={theme.colors[theme.primaryColor][6]} />,
              text: 'Change Password',
              onClick: () => openChangePasswordModal(row),
            },
            {
              // icon: accountCircleIcon,
              iconComponent: <IconUsers color={theme.colors[theme.primaryColor][6]} fill={theme.colors[theme.primaryColor][6]} />,
              text: 'Impersonate',
              onClick: () => handleImpersonate(row),
            },
            {
              iconComponent: <IconTrash color={theme.colors[theme.primaryColor][6]} />,
              text: 'Delete',
              onClick: () => openDeleteUser(row),
            },
          ]}
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Manage Users</ContentLayout.Title>
        <ContentLayout.Filters>
          <Button onClick={() => openUserModal()} variant="outline" size="lg">
            Add new
          </Button>
          <SearchBox placeholder="Search by email or name" handleSearch={handleSearch} value={filters?.searchQuery} />
        </ContentLayout.Filters>
      </ContentLayout.Header>
      <ContentLayout.SubHeader>
        <Group justify="flex-end" mb="lg">
          <ListFilterDropdown title="Declarant filter" values={declarantsOptions} onChange={filterByDeclarant} />
        </Group>
      </ContentLayout.SubHeader>
      <ContentLayout.Content>
        <Table
          columns={columns}
          dataSource={users || []}
          total={totalItems}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
          verticalSpacing="xs"
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
